import { Link } from 'react-router-dom';
import { useUserscriptLatest, useUsers } from '../api/queries';
import { ROLE_LABELS, isPlayerVerified, type User } from '../api/types';
import { StatusBadge } from '../components/Badge';

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString('de-DE') : '—';
}

/**
 * Zeigt nur etwas, wenn ein Administrator schon eine Version veröffentlicht
 * hat -- vorher lieber gar keine Box als ein toter Download-Link. Bewusst
 * ohne Verifizierungs-Sperre: das Userskript wird ja gerade für die
 * Verifizierung gebraucht (`server/src/api/routes/userscript-info.routes.ts`).
 */
function UserscriptBox() {
  const latest = useUserscriptLatest();
  const version = latest.data?.latest;
  if (!version) return null;

  return (
    <section className="card">
      <h2>Userskript</h2>
      <p className="muted">
        Aktuelle Version: <strong>{version.version}</strong> (veröffentlicht am{' '}
        {new Date(version.publishedAt).toLocaleDateString('de-DE')})
      </p>
      <a className="btn btn-primary" href="/userscript/twtool.user.js">
        Herunterladen
      </a>
      <p className="hint" style={{ marginTop: '8px' }}>
        In Tampermonkey installieren -- danach werden neue Versionen automatisch nachgeladen.
      </p>
    </section>
  );
}

export function DashboardPage({ user }: { user: User }) {
  const isAdmin = user.role === 'admin';
  const verified = isPlayerVerified(user);
  // Nur Administratoren duerfen die Liste lesen, daher die Abfrage abschalten.
  const overview = useUsers({}, { enabled: isAdmin });
  const stats = isAdmin ? overview.data?.stats : undefined;

  return (
    <>
      <section className="card">
        <h1>Willkommen, {user.username}</h1>
        <p className="muted">
          {user.world
            ? `Dein Zugang für die Welt „${user.world.name}" ist aktiv.`
            : 'Dein Zugang ist aktiv.'}
          {isAdmin ? ' Als Administrator siehst du alle Welten.' : ''}
        </p>

        <dl className="meta-grid">
          <div>
            <dt>Welt</dt>
            <dd>{user.world ? user.world.name : 'weltübergreifend'}</dd>
          </div>
          <div>
            <dt>Rolle</dt>
            <dd>{ROLE_LABELS[user.role]}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <StatusBadge status={user.status} />
            </dd>
          </div>
          <div>
            <dt>Registriert am</dt>
            <dd>{formatDate(user.createdAt)}</dd>
          </div>
          <div>
            <dt>Letzte Anmeldung</dt>
            <dd>{formatDate(user.lastLoginAt)}</dd>
          </div>
        </dl>
      </section>

      <UserscriptBox />

      {/*
       * Ohne verifizierte Spielfigur bleibt praktisch das ganze Portal
       * gesperrt (requireVerifiedPlayer serverseitig) -- das Dashboard zeigt
       * dann bewusst nur diesen Aufruf statt der üblichen Kacheln darunter.
       */}
      {!verified && (
        <section className="card card-callout">
          <h2>⚠ Spielfigur verifizieren</h2>
          <p>
            Bevor du das Portal nutzen kannst, muss deine Spielfigur verifiziert werden — das
            dauert nur einen Moment.
          </p>
          <Link className="btn btn-primary" to="/konto">
            Jetzt verifizieren
          </Link>
        </section>
      )}

      {verified && isAdmin && stats && (
        <section className="card">
          <h2>Portal-Übersicht (alle Welten)</h2>
          <div className="stat-row">
            <div className="stat">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Konten</span>
            </div>
            <div className="stat">
              <span className="stat-value stat-warn">{stats.pending}</span>
              <span className="stat-label">Warten auf Freischaltung</span>
            </div>
            <div className="stat">
              <span className="stat-value stat-ok">{stats.active}</span>
              <span className="stat-label">Freigeschaltet</span>
            </div>
            <div className="stat">
              <span className="stat-value">{stats.disabled}</span>
              <span className="stat-label">Gesperrt</span>
            </div>
          </div>
          <p className="muted">
            <Link to="/admin/welten">Welten verwalten</Link>
          </p>
          {stats.pending > 0 ? (
            <Link className="btn btn-primary" to="/admin/benutzer?status=pending">
              {stats.pending} Anmeldung(en) prüfen
            </Link>
          ) : (
            <Link className="btn" to="/admin/benutzer">
              Benutzer verwalten
            </Link>
          )}
        </section>
      )}

      {verified && (
        <section className="card">
          <h2>Nächste Schritte</h2>
          <p className="muted">
            Hier entstehen die eigentlichen Werkzeuge (Dorfverwaltung, Angriffsplanung,
            Berichtsauswertung). Der Portalrahmen mit Registrierung, Freischaltung und Rollen
            steht.
          </p>
        </section>
      )}
    </>
  );
}
